import {
    CustomerChangeName,
    formatAsFileSize,
    Identification,
    ChangeNameDocument,
    getChangeNameEndpoint,
    FileInfo,
    editViewIdentificationSectionFrontendSchema,
} from 'cpfr-common';
import {
    FileUpload,
    preventSubmit,
    Spinner,
    useAnalyticsPageViewTracker,
    ValidatedInput,
} from '@cp-shared-19/frontend-ui';
import { Button, ButtonContainer, DataOverview, Fieldset, Form, Layout, Paragraph } from '@vwfs-bronson/bronson-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';
import { maxFileSize } from 'config';
import { CpDataApi } from 'cp-xhr';
import { InitialValues, initialValues } from './initialValues';
import { getSeparatedFilesIds, uploadFilesToMediaStorage } from './helpers';
import { EmailInputModal } from '../../../request/form-view/email-input/EmailInputModal';
import { getErrorCode, isDuplicateSubmissionError, useSubmitOnce } from 'utils';

export enum EditStatus {
    NOT_PERFORMED = 'NOT_PERFORMED',
    SUCCESS = 'SUCCESS',
    ERROR = 'ERROR',
    ERROR_VIRUS = 'ERROR_VIRUS',
    NO_CHANGE_DETECTED = 'NO_CHANGE_DETECTED',
    WRONG_REASON = 'WRONG_REASON',
    SIREN_IS_MANDATORY_FOR_SME = 'SIREN_IS_MANDATORY_FOR_SME',
    ERROR_NO_EMAIL = 'EMAIL_NOT_FOUND_OR_PROVIDED',
}

export type EditStatusWithData = {
    status: EditStatus;
    identification?: Identification;
    formValues?: CustomerChangeName;
};

export type EditViewProps = {
    identification: Identification;
    cancelEditing: () => void;
    finishEditing: (newEditStatus: EditStatus, updatedIdentification?: Identification) => void;
    editStatus: EditStatus;
};

export const EditView: React.FC<EditViewProps> = ({ identification, cancelEditing, finishEditing }) => {
    const { t } = useTranslation(['my-profile', 'loader']);
    const { isSubmitting, run } = useSubmitOnce();
    const [error, setError] = useState<string | null>(null);
    const trackingSection = 'Identification';
    useAnalyticsPageViewTracker('editProfileSectionDisplayed', true, trackingSection);

    const fileUploadTranslationPrefix = 'identification.edit-view.file-upload';
    const validationPrefix = 'identification.edit-view';

    initialValues.firstName = identification.data.name ?? '';
    initialValues.lastName = identification.data.surname ?? '';

    // The latch covers the media storage upload too, not just the request: that
    // upload is where a second click used to slip through and send the change twice.
    const handleSubmit = (values: InitialValues): Promise<void> =>
        run(async () => {
            setError(null);
            const { firstName, lastName, id, proofOfIdentityChange, proofOfAddress, email } = values;
            const { name, surname, customerNumber, gender, isEligibleNpaiNotification } = identification.data;

            const hasRequiredFiles = id.length > 0 && proofOfIdentityChange.length > 0;
            if (!hasRequiredFiles) {
                return;
            }

            const documentsValues = {
                id,
                proofOfIdentityChange,
                proofOfAddress,
            };

            const mediaStorageFilesIds: Promise<ChangeNameDocument>[] = uploadFilesToMediaStorage(documentsValues);

            const doAfterUploadError = (): void => {
                finishEditing(EditStatus.ERROR);
            };

            const reportSubmitted = (): void => {
                const updatedName: Identification = {
                    customerType: identification.customerType,
                    data: {
                        name: firstName,
                        surname: lastName,
                        customerNumber,
                        gender,
                        isEligibleNpaiNotification,
                    },
                };
                finishEditing(EditStatus.SUCCESS, updatedName);
            };

            const doAfterSuccessfullUpload = (documentsIds: FileInfo[]): Promise<void> => {
                const preparedChangeValues: CustomerChangeName = {
                    customerType: identification.customerType,
                    firstName: name === firstName ? '' : firstName,
                    lastName: surname === lastName ? '' : lastName,
                    documents: [...documentsIds],
                    email: email || identification.data.email || '',
                };

                return CpDataApi.put(getChangeNameEndpoint(), preparedChangeValues)
                    .then(reportSubmitted)
                    .catch((error) => {
                        // A duplicate is not a failure: the change is already registered.
                        if (isDuplicateSubmissionError(error)) {
                            reportSubmitted();
                            return;
                        }
                        const errorCode = getErrorCode(error);
                        setError(errorCode ?? null);
                        switch (errorCode) {
                            case 'NO_CHANGE_DETECTED':
                                finishEditing(EditStatus.NO_CHANGE_DETECTED);
                                break;
                            case 'MARKET_API_NOT_REACHABLE':
                                finishEditing(EditStatus.ERROR);
                                break;
                            case 'WRONG_REASON':
                                finishEditing(EditStatus.WRONG_REASON);
                                break;
                            case 'VIRUS':
                                finishEditing(EditStatus.ERROR_VIRUS);
                                break;
                            case 'EMAIL_NOT_FOUND_OR_PROVIDED':
                                //finishEditing(EditStatus.ERROR_NO_EMAIL);
                                break;
                            default:
                                finishEditing(EditStatus.ERROR);
                                break;
                        }
                    });
            };

            // Returned, so the latch is only released once the request answered.
            return Promise.all(mediaStorageFilesIds)
                .then((filesId: ChangeNameDocument[]) => {
                    const separatedDocumentsIds: FileInfo[] = getSeparatedFilesIds(filesId);
                    return doAfterSuccessfullUpload(separatedDocumentsIds);
                })
                .catch(() => {
                    doAfterUploadError();
                });
        });

    const getFileUploadsList = (): { name: string; suffix: string }[] => {
        return [
            { name: 'id', suffix: 'id' },
            { name: 'proofOfIdentityChange', suffix: 'proof-of-identity-change' },
        ];
    };

    const errorMessages = {
        firstName: {
            required: t(`identification.edit-view.validation-error.required-input`),
            matches: t(`${validationPrefix}.validation-error.no-numbers`),
        },
        lastName: {
            required: t(`identification.edit-view.validation-error.required-input`),
            matches: t(`${validationPrefix}.validation-error.no-numbers`),
        },
        id: {
            required: t(`${fileUploadTranslationPrefix}.validation.required`),
            max: t(`${fileUploadTranslationPrefix}.validation.wrong-number-of-files`),
        },
        proofOfIdentityChange: {
            required: t(`${fileUploadTranslationPrefix}.validation.required`),
            max: t(`${fileUploadTranslationPrefix}.validation.wrong-number-of-files`),
        },
        email: {
            required: t(`${validationPrefix}.validation-error.required`),
            mustBeEmail: t(`${validationPrefix}.validation-error.mustBeEmail`),
        },
    };

    return (
        <DataOverview titleLevel={2} title={t('identification.edit-view.title')}>
            <Paragraph className={'u-mb'}>{t('identification.edit-view.description')}</Paragraph>
            {isSubmitting && (
                <Spinner center>
                    <p>{t('genericText', { ns: 'loader' })}</p>
                </Spinner>
            )}
            <Formik
                initialValues={initialValues}
                validationSchema={editViewIdentificationSectionFrontendSchema(
                    errorMessages,
                    error === 'EMAIL_NOT_FOUND_OR_PROVIDED',
                )}
                onSubmit={handleSubmit}
            >
                {({ handleSubmit, errors, isValid }) => (
                    <Form onSubmit={(e) => preventSubmit(e)} data-testid="edit-form">
                        <Fieldset>
                            <Fieldset.Row>
                                <Layout>
                                    <Layout.Item default="1/2" s="1/1">
                                        <ValidatedInput
                                            className="c-form-field--no-floating"
                                            label={t('identification.edit-view.first-name')}
                                            name="firstName"
                                            testId="name"
                                            inputId="first-name-my-profile-identification-section"
                                            type="text"
                                            autocomplete="given-name"
                                            aria-required={true}
                                            requiredText=" *"
                                            isMandatory={true}
                                        />
                                    </Layout.Item>
                                    <Layout.Item default="1/2" s="1/1">
                                        <ValidatedInput
                                            className="c-form-field--no-floating"
                                            label={t('identification.edit-view.last-name')}
                                            name="lastName"
                                            testId="surname"
                                            inputId="surname-my-profile-identification-section"
                                            type="text"
                                            autocomplete="family-name"
                                            aria-required={true}
                                            requiredText=" *"
                                            isMandatory={true}
                                        />
                                    </Layout.Item>
                                </Layout>
                            </Fieldset.Row>
                        </Fieldset>
                        <Fieldset.Row>
                            <Layout>
                                {getFileUploadsList().map(({ name, suffix }) => {
                                    return (
                                        <Layout.Item key={name}>
                                            <FileUpload
                                                name={name}
                                                uploadItemsLabel={''}
                                                descriptionText={t(`${fileUploadTranslationPrefix}.${suffix}`)}
                                                buttonText={t(`${fileUploadTranslationPrefix}.button`)}
                                                cancelLabel={t('translation:editable-section-nav.cancel')}
                                                fileSizeFormatter={(bytes: number): string =>
                                                    formatAsFileSize(bytes, 2)
                                                }
                                                maxFileSize={maxFileSize}
                                                multiple={false}
                                                validFileFormats={['pdf']}
                                                sizeError={t(`${fileUploadTranslationPrefix}.validation.wrong-size`)}
                                                typeError={t(`${fileUploadTranslationPrefix}.validation.wrong-type`)}
                                                sizeAndTypeError={
                                                    <>
                                                        {t(`${fileUploadTranslationPrefix}.validation.wrong-size`)}
                                                        <br aria-hidden="true" />
                                                        {t(`${fileUploadTranslationPrefix}.validation.wrong-type`)}
                                                    </>
                                                }
                                            />
                                        </Layout.Item>
                                    );
                                })}
                            </Layout>
                        </Fieldset.Row>
                        <Fieldset>
                            <Fieldset.Row>
                                <ButtonContainer center>
                                    <Button secondary onClick={cancelEditing} testId="cancel-button" type="button">
                                        {t('identification.edit-view.button.cancel')}
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            if (isSubmitting || Object.keys(errors).length > 0 || !isValid) {
                                                return;
                                            }

                                            handleSubmit();
                                        }}
                                        disabled={isSubmitting || Object.keys(errors).length > 0 || !isValid}
                                        testId="submit-button"
                                        type="button"
                                    >
                                        {t('identification.edit-view.button.confirm')}
                                    </Button>
                                </ButtonContainer>
                            </Fieldset.Row>
                        </Fieldset>
                        <EmailInputModal
                            show={error === 'EMAIL_NOT_FOUND_OR_PROVIDED'}
                            onClose={() => setError(null)}
                            onConfirm={() => handleSubmit()}
                        />
                    </Form>
                )}
            </Formik>
        </DataOverview>
    );
};
